import React, { useMemo, useState } from 'react';

import type { UUID } from '@aline/common';
import type { IconButtonProps } from '@mui/material';
import { Grid, IconButton, InputLabel, Typography } from '@mui/material';
import type {
  GridColDef,
  GridRenderCellParams,
  GridSortModel,
  GridValidRowModel,
} from '@mui/x-data-grid-premium';
import {
  DataGridPremium,
  gridRowsLookupSelector,
  useGridApiContext,
  useGridSelector,
} from '@mui/x-data-grid-premium';
import {
  AlineCheckbox,
  AlineTooltip,
  CheckMarkMenuItem,
  ConfirmationDialog,
  FiltersToolbarButton,
  getStringOperatorsWithSafeIsAnyOf,
  ListCountFooter,
  ListEmpty,
  MergeDialog,
  NameDialog,
  NameDialogVariant,
  NewContractTypeButton,
  ReportFiltersPanel,
  SearchInput,
  Spacer,
  ToolbarSelect,
  useListToolbarStyles,
} from 'src/components';
import { useAccessGuard } from 'src/components/auth';
import { GridAlineTooltip } from 'src/components/generic/data-grid/grid-aline-tooltip';
import { MergeIcon } from 'src/components/icons/MergeIcon';
import { Apollo } from 'src/gql/generated';
import { useDispatchSuccessSnack } from 'src/helpers';
import {
  createDataGridPremiumPaperStyles,
  FiltersAnchorProvider,
  useDataGridSearch,
  useDeleteContractType,
  useFiltersAnchorState,
  useGetMyContractTypesWithDocCount,
  useGetSelectedRowIds,
  useMergeContractTypes,
  useUpdateContractType,
} from 'src/hooks';
import { displayOnHover } from 'src/theme';
import { makeStyles } from 'tss-react/mui';
import * as Yup from 'yup';

import { ContractTypesMenu } from './ContractTypesMenu';

export const minWidthTitleCellPx = 350;
const useStyles = makeStyles()(() => ({
  grid: {
    height: '100%',
  },
  header: {
    alignItems: 'center',
    display: 'flex',
    justifyContent: 'space-between',
  },
  listContainer: {
    display: 'flex',
    flexDirection: 'column',
    flexGrow: 1,
    minHeight: 0,
    position: 'relative',
  },
  xGrid: {
    '& .MuiDataGrid-row': {
      [`&:hover .${displayOnHover}`]: {
        opacity: 1,
        pointerEvents: 'auto',
      },
      '&:hover #overflow-menu': {
        visibility: 'visible',
      },
    },
    flexGrow: 1,
    minHeight: 0,
  },
}));

type ContractTypeRow = Pick<Apollo.ContractType, 'documentCount' | 'id' | '__typename' | 'value'>;

type ContractTypeColDef = GridColDef<ContractTypeRow>;

export function ContractTypesTab(): JSX.Element {
  const { classes } = useStyles();
  const { anchorEl: filtersAnchorEl, setAnchorEl: setFiltersAnchorEl } = useFiltersAnchorState();
  const { loading, myContractTypes } = useGetMyContractTypesWithDocCount({
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
  });
  const [sortModel, setSortModel] = useState<GridSortModel>([
    { field: 'value' satisfies keyof ContractTypeRow, sort: 'asc' },
  ]);
  const columnDefs = useMemo((): ContractTypeColDef[] => {
    return [
      {
        field: 'value',
        filterOperators: getStringOperatorsWithSafeIsAnyOf(),
        filterable: true,
        headerName: 'Contract Type',
        minWidth: minWidthTitleCellPx,
        renderCell: TitleCell,
        type: 'string',
      },
      {
        field: 'documentCount',
        flex: 1,
        headerName: 'Documents Using',
        renderCell: CountCell,
      },
    ];
  }, []);

  return (
    <FiltersAnchorProvider setAnchorEl={setFiltersAnchorEl}>
      <div className={classes.listContainer}>
        <DataGridPremium
          checkboxSelection
          disableColumnMenu
          disableColumnResize
          disableRowSelectionOnClick
          showToolbar
          className={classes.xGrid}
          columns={columnDefs}
          loading={loading}
          rows={myContractTypes}
          slotProps={{
            filterPanel: {
              filterableColumns: 'visible',
            },
            footer: {
              noun: 'contract types',
              totalCount: myContractTypes.length,
            },
            loadingOverlay: {
              noRowsVariant: 'linear-progress',
              variant: 'linear-progress',
            },
            panel: {
              placement: 'bottom-start',
              target: filtersAnchorEl ?? undefined,
            },
          }}
          slots={{
            baseCheckbox: AlineCheckbox,
            baseTooltip: GridAlineTooltip,
            filterPanel: ReportFiltersPanel,
            footer: ListCountFooter,
            noRowsOverlay: TypeListEmpty,
            toolbar: TypesListToolbar,
          }}
          sortModel={sortModel}
          sx={createDataGridPremiumPaperStyles}
          onSortModelChange={setSortModel}
        />
      </div>
    </FiltersAnchorProvider>
  );
}

function TypeListEmpty(): JSX.Element {
  return <ListEmpty subtitle="Click new to create a contract type." title="No Contract Types" />;
}

const useCellStyles = makeStyles()(theme => ({
  deleteContainer: {
    marginTop: theme.spacing(2),
  },
  deletedColName: {
    textDecoration: 'line-through',
  },
  inputLabel: {
    ...theme.appTypography.fieldLabel,
    marginBottom: theme.spacing(2),
  },
  optionsContainer: {
    alignItems: 'center',
    // v9 Grid (was Grid2) doesn't default container to width:100%; without
    // this `justifyContent: space-between` has no extra space to distribute
    // and the menu button collapses next to the count.
    width: '100%',
  },
}));

function TitleCell({ row }: GridRenderCellParams<ContractTypeRow>): JSX.Element {
  return <Typography noWrap>{row.value}</Typography>;
}

function CountCell({ row }: GridRenderCellParams<ContractTypeRow>): JSX.Element {
  const { classes } = useCellStyles();
  const apiRef = useGridApiContext();
  const { updateContractType } = useUpdateContractType();
  const { deleteContractType } = useDeleteContractType();
  const [renameDialogOpen, setRenameDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const gridRowsLookup = useGridSelector(apiRef, gridRowsLookupSelector);
  const [destinationTypeId, setDestinationTypeId] = useState<UUID | null>(null);
  const [destinationTypeOpen, setDestinationTypeOpen] = useState<boolean>(false);
  const contractTypeSchema = useGetContractTypeSchema({ fetchPolicy: 'cache-only' });
  const myContractTypes = Object.values(gridRowsLookup).filter(getIsContractTypeRow);
  const dispatchDeleteSuccessSnack = useDispatchSuccessSnack({
    message: 'Contract type successfully deleted.',
  });
  const dispatchUpdateSuccessSnack = useDispatchSuccessSnack({
    message: 'Contract type name successfully updated!',
  });

  async function handleDelete(): Promise<void> {
    if (!destinationTypeId) return;

    const { data } = await deleteContractType({
      variables: { input: { deletedTypeId: row.id, newTypeId: destinationTypeId } },
    });
    if (data) dispatchDeleteSuccessSnack();
  }

  async function handleUpdate(value: string): Promise<void> {
    const { data } = await updateContractType({
      variables: { input: { id: row.id, props: { value } } },
    });

    setRenameDialogOpen(false);
    if (data) dispatchUpdateSuccessSnack();
  }

  return (
    <>
      <Grid container className={classes.optionsContainer} sx={{ justifyContent: 'space-between' }}>
        <Typography noWrap>{row.documentCount}</Typography>
        <ContractTypesMenu
          setDeleteDialogOpen={setDeleteDialogOpen}
          setRenameDialogOpen={setRenameDialogOpen}
          showDelete={myContractTypes.length > 1}
        />
      </Grid>
      <NameDialog
        handleClose={() => setRenameDialogOpen(false)}
        initialName={row.value}
        open={renameDialogOpen}
        placeholder="Enter contract type..."
        validationSchema={contractTypeSchema}
        variant={NameDialogVariant.ContractType}
        onFormikSubmit={handleUpdate}
      />
      <ConfirmationDialog
        message={`Are you sure you want to delete "${row.value}" type? Please pick a new type for all documents that use "${row.value}" type.`}
        open={deleteDialogOpen}
        primaryDisabledReason={!destinationTypeId ? 'Must select a new type' : ''}
        title="Delete contract type"
        onClickPrimary={handleDelete}
        onClose={() => setDeleteDialogOpen(false)}
      >
        <Grid container className={classes.deleteContainer} spacing={2} wrap="nowrap">
          <Grid size={6}>
            <InputLabel className={classes.inputLabel}>
              This contract type will be deleted:
            </InputLabel>
            <Typography className={classes.deletedColName} variant="subtitle1">
              {row.value}
            </Typography>
          </Grid>
          <Grid size={6}>
            <InputLabel className={classes.inputLabel}>
              Change documents to use this type:
            </InputLabel>
            <ToolbarSelect
              fullWidth
              MenuProps={{
                anchorOrigin: { horizontal: 'right', vertical: 'bottom' },
                transformOrigin: { horizontal: 'right', vertical: 'top' },
              }}
              customRenderValue={
                myContractTypes.find(contractType => contractType.id === destinationTypeId)
                  ?.value ?? 'Select a type'
              }
              open={destinationTypeOpen}
              value={destinationTypeId}
              onClose={() => setDestinationTypeOpen(false)}
              onOpen={() => setDestinationTypeOpen(true)}
              onSelectChange={e => setDestinationTypeId(e.target.value as UUID)}
            >
              {myContractTypes
                .filter(contractType => contractType.id !== row.id)
                .map(contractType => (
                  <CheckMarkMenuItem
                    key={contractType.id}
                    selected={destinationTypeId === contractType.id}
                    value={contractType.id}
                  >
                    {contractType.value}
                  </CheckMarkMenuItem>
                ))}
            </ToolbarSelect>
          </Grid>
        </Grid>
      </ConfirmationDialog>
    </>
  );
}

function TypesListToolbar(): JSX.Element {
  const apiRef = useGridApiContext();
  const { classes } = useListToolbarStyles();
  const { search, setSearch } = useDataGridSearch();
  const { selectedRowIds } = useGetSelectedRowIds();
  const { mergeContractTypes } = useMergeContractTypes();
  const gridRowsLookup = useGridSelector(apiRef, gridRowsLookupSelector);
  const [mergeDialogOpen, setMergeDialogOpen] = useState<boolean>(false);
  const myContractTypes = Object.values(gridRowsLookup).filter(getIsContractTypeRow);
  const showMergeButton = myContractTypes.length >= 2 && selectedRowIds.length >= 1;
  const handleOpenMergeDialog = useAccessGuard({
    callback: () => setMergeDialogOpen(!mergeDialogOpen),
    minRole: Apollo.OrgRole.Admin,
  });

  async function handleMergeTypes(targetTypeId: UUID, sourceTypeIds: UUID[]) {
    return mergeContractTypes({ variables: { input: { sourceTypeIds, targetTypeId } } });
  }

  return (
    <div className={classes.toolbar}>
      <FiltersToolbarButton />
      <Spacer />
      {showMergeButton && (
        <AlineTooltip arrow placement="top" title="Merge types">
          <div>
            <SmallMergeIconButton onClick={handleOpenMergeDialog} />
          </div>
        </AlineTooltip>
      )}
      <SearchInput placeholder="Search types..." search={search} setSearch={setSearch} />
      <NewContractTypeButton />
      <MergeDialog
        entities={myContractTypes}
        getDisabledReason={targetId => {
          return !targetId ? 'Select a target type' : '';
        }}
        handleMerge={handleMergeTypes}
        message={`Are you sure you want to merge? All selected contract types will be deleted and merged into the target type. This action cannot be undone.`}
        open={mergeDialogOpen}
        selectedIds={selectedRowIds}
        title="Merge types?"
        onClose={() => setMergeDialogOpen(false)}
      />
    </div>
  );
}

export const useMergeButtonStyles = makeStyles()(_theme => ({
  mergeButton: {
    padding: 6,
  },
}));

export function SmallMergeIconButton(props: IconButtonProps): JSX.Element {
  const { classes } = useMergeButtonStyles();

  return (
    <IconButton className={classes.mergeButton} {...props} size="large">
      <MergeIcon />
    </IconButton>
  );
}

export function useGetContractTypeSchema(
  options?: Parameters<typeof Apollo.useGetMyContractTypesWithDocumentCountQuery>[0]
) {
  const { myContractTypes } = useGetMyContractTypesWithDocCount(options);
  const lowerCaseTypeNames = myContractTypes.map(t => t.value.toLowerCase());

  return useMemo(() => {
    return Yup.object({
      name: Yup.string()
        .required('Required')
        .test('unique-validation', 'Type name must be unique', newContractType => {
          const lowerCaseContractType = newContractType.toLowerCase();
          const allLowercaseTypes = [lowerCaseContractType, ...lowerCaseTypeNames];
          const typeCount = allLowercaseTypes.filter(
            typeName => typeName === lowerCaseContractType
          ).length;
          return typeCount <= 1;
        }),
    });
  }, [lowerCaseTypeNames]);
}

function getIsContractTypeRow(row: GridValidRowModel): row is ContractTypeRow {
  return (row as ContractTypeRow).__typename === 'ContractType';
}
